import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Directive, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { ProviderConfigService } from '../services/provider-config.service';
import { ProviderService } from '../services/provider.service';
import { TenantService } from '../../admin/tenants/services/tenant.service';
import {
  CreateProviderConfigRequest,
  Provider,
  ProviderConfig,
  UpdateProviderConfigRequest,
} from '../models/provider.model';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumb.service';
import { ToastService } from '../../../shared/services/toast-service';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { SelectOption } from '../../../shared/components/forms/select';
import { effectiveFeedStatus, feedStatusDot } from '../util/feed-status.util';
import { TENANT_PICKER_SIZE, providerBasePath } from './provider-routing';
import { ConfigViolationCode, configViolationsOf } from '../util/config-schema.util';

/**
 * Base for the per-type feed pages (/admin/providers/[<type>/]:providerId/:feedId)
 * — the edit surface for one feed, and (feedId === 'new') the create surface.
 * A full page outside the provider shell (same idiom as AdminUserDetailComponent
 * below the tenant shell), so it fetches the provider itself.
 *
 * Holds everything that isn't type-specific: provider/feed/tenant loading,
 * name/locality/tenant form state, status, Save (create/update, 409 → name
 * taken, 400 with violations → `configViolations` + a toast naming the keys) and Delete. A subclass supplies the configuration editor through
 * `configurationValid()` / `buildConfiguration()` / `applyConfiguration()`,
 * and provides itself as `FeedPage` so the shared header, basics and identity
 * cards can read this state:
 *
 *   providers: [{ provide: FeedPage, useExisting: forwardRef(() => XFeedPageComponent) }]
 */
@Directive()
export abstract class FeedPage implements OnInit {
  protected readonly configService = inject(ProviderConfigService);
  protected readonly providerService = inject(ProviderService);
  protected readonly tenantService = inject(TenantService);
  protected readonly breadcrumbs = inject(BreadcrumbService);
  protected readonly toastService = inject(ToastService);
  protected readonly modal = inject(ModalService);
  protected readonly transloco = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);

  /** The route tree this page is mounted on: a provider type, or null for the generic tree. */
  abstract readonly routeType: string | null;

  readonly providerId = this.route.snapshot.paramMap.get('providerId') ?? '';
  /** The raw :feedId segment — a feed id, or 'new' on the create surface. */
  protected readonly routeFeedId = this.route.snapshot.paramMap.get('feedId') ?? '';
  readonly isCreate = this.routeFeedId === 'new';

  readonly provider = signal<Provider | null>(null);
  readonly feed = signal<ProviderConfig | null>(null);
  /** Provider's verdict on the current configuration; null until the feed loads. */
  readonly feedStatus = computed(() => {
    const f = this.feed();
    return f ? effectiveFeedStatus(f) : null;
  });
  readonly feedStatusDot = computed(() => {
    const status = this.feedStatus();
    return status ? feedStatusDot(status) : null;
  });
  readonly tenantOptions = signal<SelectOption[]>([]);
  readonly tenantsLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isLoading = signal(true);
  /** Set from a 409 on save: names are unique per tenant + provider, ignoring
   *  case. Cleared as soon as the name is edited. */
  readonly nameTaken = signal(false);
  /** Core's verdict on the configuration from the last save (a 400 listing violations), by key.
   *  The schema-driven form shows them next to the fields and drops a key once it's edited. */
  readonly configViolations = signal<Record<string, ConfigViolationCode>>({});

  // Form state shared by every type
  readonly name = signal('');
  readonly locality = signal('');
  /** Only editable on create; tenant and provider are fixed once a feed exists. */
  readonly tenantId = signal('');

  readonly tenantName = computed(() => {
    const f = this.feed();
    if (!f) return '';
    return this.tenantOptions().find((o) => o.value === f.tenantId)?.label ?? f.tenantId;
  });

  /** Whether the type-specific configuration editor holds a saveable value. */
  protected abstract configurationValid(): boolean;
  /** The configuration map to save, built from the type-specific editor. */
  protected abstract buildConfiguration(): Record<string, string>;
  /** Load a feed's saved configuration into the type-specific editor. */
  protected abstract applyConfiguration(configuration: Record<string, string> | null): void;

  /** A getter, not a computed: subclasses may keep editor state in plain
   *  ngModel fields, so this has to be re-read on every check. */
  get isValid(): boolean {
    return (
      !!this.name().trim() &&
      !!this.provider() &&
      this.configurationValid() &&
      (!this.isCreate || !!this.tenantId().trim())
    );
  }

  ngOnInit(): void {
    this.fetchProvider();
    this.fetchTenants();
    if (this.isCreate) {
      this.isLoading.set(false);
      this.breadcrumbs.setLabel('feedId', 'new', this.transloco.translate('breadcrumb.newFeed'));
    } else {
      this.fetchFeed();
    }
  }

  /** Router commands for the provider's Feeds tab. */
  basePath(): string[] {
    return providerBasePath(this.routeType, this.providerId);
  }

  discard(): void {
    const f = this.feed();
    if (!f) return;
    this.applyFeedToForm(f);
  }

  save(): void {
    if (!this.isValid) return;
    const configuration = this.buildConfiguration();
    this.isSaving.set(true);

    if (this.isCreate) {
      const req: CreateProviderConfigRequest = {
        providerId: this.providerId,
        tenantId: this.tenantId().trim(),
        name: this.name().trim(),
        configuration,
        locality: this.locality().trim() || undefined,
      };
      this.configService
        .create(req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.toastService.show(this.transloco.translate('providers.feedPage.toast.created'), 'success');
            this.isSaving.set(false);
            this.navigateToCreatedFeed(res.data.id);
          },
          error: (err: HttpErrorResponse) => this.onSaveError(err, 'providers.feedPage.toast.createFailed'),
        });
    } else {
      const feed = this.feed();
      if (!feed) return;
      const req: UpdateProviderConfigRequest = {
        name: this.name().trim(),
        configuration,
        locality: this.locality().trim() || undefined,
      };
      this.configService
        .update(feed.id, req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.toastService.show(this.transloco.translate('providers.feedPage.toast.updated'), 'success');
            this.isSaving.set(false);
            this.feed.set(res.data);
            this.breadcrumbs.setLabel('feedId', res.data.id, res.data.name);
          },
          error: (err: HttpErrorResponse) => this.onSaveError(err, 'providers.feedPage.toast.updateFailed'),
        });
    }
  }

  async confirmDelete(): Promise<void> {
    const feed = this.feed();
    if (!feed) return;
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.feedPage.delete.title'),
      message: this.transloco.translate('providers.feedPage.delete.message', { name: feed.name }),
      confirmLabel: this.transloco.translate('providers.feedPage.delete.confirm'),
      confirmWord: feed.name,
    });
    if (!confirmed) return;
    this.configService
      .delete(feed.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.feedPage.toast.deleted'), 'success');
          void this.router.navigate(this.basePath());
        },
        error: () => this.toastService.show(this.transloco.translate('providers.feedPage.toast.deleteFailed'), 'error'),
      });
  }

  /** Extra navigation options for landing on a newly created feed (e.g. a
   *  query param picking a tab). */
  protected createdFeedNavigationExtras(): NavigationExtras {
    return {};
  }

  /**
   * After a create, open the new feed's page. `…/new` → `…/<id>` is the same
   * ':feedId' route config, so the router would reuse this create-mode
   * instance (params are read once, from the snapshot) and the page would stay
   * in create mode. Hopping through the provider page first — without touching
   * the URL — makes the router build the feed page fresh.
   */
  private navigateToCreatedFeed(feedId: string): void {
    void this.router
      .navigate(this.basePath(), { skipLocationChange: true })
      .then(() => this.router.navigate([...this.basePath(), feedId], this.createdFeedNavigationExtras()));
  }

  private onSaveError(err: HttpErrorResponse, toastKey: string): void {
    this.isSaving.set(false);
    if (err.status === 409) {
      this.nameTaken.set(true);
      return;
    }
    const violations = configViolationsOf(err);
    if (violations) {
      this.configViolations.set(Object.fromEntries(violations.map((v) => [v.key, v.code])));
      this.toastService.show(
        this.transloco.translate('providers.feedPage.toast.invalidConfiguration', {
          keys: [...new Set(violations.map((v) => v.key))].join(', '),
        }),
        'error',
      );
      return;
    }
    this.toastService.show(this.transloco.translate(toastKey), 'error');
  }

  private applyFeedToForm(f: ProviderConfig): void {
    this.configViolations.set({});
    this.tenantId.set(f.tenantId);
    this.name.set(f.name);
    this.locality.set(f.locality ?? '');
    this.applyConfiguration(f.configuration);
  }

  private fetchProvider(): void {
    if (!this.providerId) return;
    this.providerService
      .get(this.providerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.provider.set(res.data);
          this.breadcrumbs.setLabel('providerId', res.data.id, res.data.name);
        },
        error: () => {},
      });
  }

  /** Re-read the feed after its files changed, to update server-derived parts
   *  (the files summary, revision, status) without touching the form — unsaved
   *  edits survive. Silent on failure; the data is merely stale. */
  refreshFeedSummary(): void {
    if (this.isCreate) return;
    this.configService
      .get(this.routeFeedId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.feed.set(res.data), error: () => {} });
  }

  private fetchFeed(): void {
    this.configService
      .get(this.routeFeedId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.feed.set(res.data);
          this.applyFeedToForm(res.data);
          this.breadcrumbs.setLabel('feedId', res.data.id, res.data.name);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('providers.feedPage.toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  private fetchTenants(): void {
    this.tenantsLoading.set(true);
    this.tenantService
      .getTenants(0, TENANT_PICKER_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tenantOptions.set(res.data.map((tenant) => ({ value: tenant.id, label: tenant.name })));
          this.tenantsLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('providers.feedPage.toast.tenantsLoadFailed'), 'error');
          this.tenantsLoading.set(false);
        },
      });
  }
}
